/**
 * tf-core — Bundle centrale: auth state + API client + utils + types.
 *
 * Caricato come `<script type="module">` in tutte le pagine app.
 * Espone window globals per backward-compat con inline scripts legacy.
 *
 * Migration roadmap:
 *   Sprint 2 (oggi): tf-core.js disponibile + window globals esposti
 *   Sprint 3-4: Inline scripts cambiano da `localStorage.getItem('turbofattura_token')`
 *               a `window.tfAuth.getToken()` o import statement
 *   Future: rimozione localStorage in favore di cookie HttpOnly only
 *
 * @since v9.16 (Fase 4 Sprint 2)
 */

// State management
import * as auth from './state/auth';

// API client
import { apiClient, ApiClient, ApiClientError } from './api/client';

// Utils
import * as format from './utils/format';
import * as fattura from './utils/fattura';
import * as cliente from './utils/cliente';

// Modules (Sprint 5)
import * as theme from './modules/tf-theme';
import * as guard from './modules/tf-auth-guard';

// PWA (Sprint 8)
import * as pwa from './modules/tf-pwa';

// i18n (Sprint 9)
import * as i18n from './i18n';

// (Sprint 9.5 rollback): tf-sidebar-noflash + tf-view-transitions disabilitati.
// I tweak avevano introdotto inconsistenza nel rendering della sidebar.
// Mantenuto solo il fix CSS selector in tf-soft-nav.js (necessario per PJAX).
// import './modules/tf-sidebar-noflash';
// import './modules/tf-view-transitions';

// Types
import type { User, Cliente, Fattura, Prodotto, ApiResponse, LoginResponse } from './types';

// ============================================================
// Window globals per backward compatibility
// ============================================================

declare global {
  interface Window {
    /** TurboFattura core API esposto come singleton */
    tfCore?: TfCoreAPI;
    /** Shorthand per auth state */
    tfAuth?: typeof auth;
    /** Shorthand per format helpers */
    tfFormat?: typeof format;
    /** Shorthand per API client */
    tfApi?: typeof apiClient;
  }
}

interface TfCoreAPI {
  version: string;
  auth: typeof auth;
  api: typeof apiClient;
  format: typeof format;
  fattura: typeof fattura;
  cliente: typeof cliente;
  theme: typeof theme;
  guard: typeof guard;
  pwa: typeof pwa;
  i18n: typeof i18n;
  ApiClient: typeof ApiClient;
  ApiClientError: typeof ApiClientError;
}

const VERSION = '9.16.0';

const tfCore: TfCoreAPI = {
  version: VERSION,
  auth,
  api: apiClient,
  format,
  fattura,
  cliente,
  theme,
  guard,
  pwa,
  i18n,
  ApiClient,
  ApiClientError,
};

// ============================================================
// Bootstrap (esposizione globali idempotente)
// ============================================================

if (typeof window !== 'undefined') {
  window.tfCore = tfCore;
  window.tfAuth = auth;
  window.tfFormat = format;
  window.tfApi = apiClient;

  // Log version una sola volta in console (DX dev)
  if (!(window as Window & { _tfCoreLogged?: boolean })._tfCoreLogged) {
    (window as Window & { _tfCoreLogged?: boolean })._tfCoreLogged = true;
    console.info(
      `%cTurboFattura Core v${VERSION}`,
      'color: #f59e0b; font-weight: 700; font-size: 12px;',
      '— auth, api, format helpers loaded'
    );
  }
}

// ============================================================
// Re-export per import ES module diretto
// ============================================================

export { auth, apiClient, format, fattura, cliente, theme, guard, pwa, i18n, ApiClient, ApiClientError, tfCore };
export type { User, Cliente, Fattura, Prodotto, ApiResponse, LoginResponse };
export default tfCore;
